import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, Clock, XCircle, Handshake } from 'lucide-react';
import {
  formatMoney,
  friendlyError,
  getCheckoutSession,
  simulateCheckoutSession,
} from '@/lib/api';
import { toast } from 'sonner';

// Clave única compartida con CheckoutOutcomes.jsx. Se usa exclusivamente
// para reconstruir el contexto del retorno post-pago (Redsys UrlOK/UrlKO
// no conservan el `next` de la URL original). Nunca se usa para conceder
// acceso: eso lo decide siempre el backend al leer el estado real de la
// CheckoutSession.
// Se guarda en sessionStorage (no localStorage) para que el contexto sea
// temporal, pertenezca a la pestaña que inició el checkout y no lo pisen
// otras pestañas con compras simultáneas.
const CHECKOUT_RETURN_STORAGE_KEY = 'ns_pending_checkout_return';

function persistCheckoutReturnContext({ sessionId, next, kind }) {
  try {
    const payload = { session_id: sessionId, next: next || '', kind: kind || null, ts: Date.now() };
    window.sessionStorage.setItem(CHECKOUT_RETURN_STORAGE_KEY, JSON.stringify(payload));
  } catch { /* silencioso: private mode / storage lleno */ }
}

const OUTCOMES = [
  { value: 'confirmed', label: 'Pagar ahora', variant: 'primary', icon: CheckCircle2 },
  { value: 'failed', label: 'Simular fallo', variant: 'destructive', icon: AlertTriangle },
  { value: 'cancelled', label: 'Cancelar pago', variant: 'outline', icon: XCircle },
  { value: 'pending', label: 'Dejar pendiente', variant: 'ghost', icon: Clock },
];

const STATUS_REDIRECT = {
  confirmed: '/checkout/exito',
  failed: '/checkout/error',
  cancelled: '/checkout/error',
  pending: '/checkout/pendiente',
  expired: '/checkout/error',
};

const SOURCE_LABEL = {
  event: 'Partido (legacy)',
  plan: 'Plan de suscripción',
  download: 'Descarga',
  content: 'Contenido (compra individual)',
};

export default function CheckoutSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyOutcome, setBusyOutcome] = useState(null);

  // `next`: ruta a la que redirigir tras checkout confirmado.
  const nextParam = React.useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get('next') || '';
  }, [location.search]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCheckoutSession(id);
        if (alive) setSession(data);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  // Redsys: si la sesión trae `redsys_form`, redirigimos al TPV mediante POST
  // auto-submit. NUNCA confirma el pago aquí: la confirmación real llega
  // por el webhook server-to-server.
  useEffect(() => {
    if (!session || session.provider !== 'redsys') return;
    if (session.status !== 'pending') return;
    const form = session.redsys_form;
    if (!form || !form.action_url || !form.fields) return;
    // Persistimos el contexto de retorno ANTES del submit para que
    // /checkout/exito y /checkout/error puedan reconstruir a dónde volver
    // (Redsys UrlOK/UrlKO son fijas y no conservan el `next`).
    const kind = (session.source_type === 'content_download' || session.source_type === 'download')
      ? 'download' : null;
    persistCheckoutReturnContext({ sessionId: id, next: nextParam, kind });
    const el = document.createElement('form');
    el.method = 'POST';
    el.action = form.action_url;
    el.style.display = 'none';
    Object.entries(form.fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = String(value);
      el.appendChild(input);
    });
    document.body.appendChild(el);
    // Pequeño delay para que el usuario vea el banner "Redirigiendo"
    // y, si procede, lea el aviso de apoyo al club antes de abandonar
    // la página. Se amplía a 2000 ms sin tocar firma/formulario Redsys.
    const t = setTimeout(() => el.submit(), 2000);
    return () => { clearTimeout(t); el.remove(); };
  }, [session, id, nextParam]);

  const handleOutcome = async (outcome) => {
    setBusyOutcome(outcome);
    try {
      const res = await simulateCheckoutSession(id, outcome);
      const newStatus = (res.session && res.session.status) || outcome;
      const path = STATUS_REDIRECT[newStatus] || '/checkout/error';
      // Propagar `next` para que la pantalla de éxito pueda redirigir al watch.
      const qs = new URLSearchParams({ session_id: id });
      if (nextParam) qs.set('next', nextParam);
      // Si la sesión es de descarga (content_download o event download legacy),
      // marcarla en la URL para que /checkout/exito muestre el botón
      // "Descargar" en lugar de "Reproducir contenido".
      const isDownload = session?.source_type === 'content_download' || session?.source_type === 'download';
      if (isDownload) {
        qs.set('kind', 'download');
      }
      // Persistimos también en sessionStorage para uniformar el retorno con
      // el flujo Redsys real (donde el query-string no está garantizado).
      persistCheckoutReturnContext({
        sessionId: id, next: nextParam, kind: isDownload ? 'download' : null,
      });
      navigate(`${path}?${qs.toString()}`);
    } catch (err) {
      toast.error(friendlyError(err));
      setBusyOutcome(null);
    }
  };

  return (
    <MainLayout>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold mb-2">
            Checkout de prueba
          </p>
          <h1 className="text-3xl font-bold text-[var(--ns-text)]" data-testid="checkout-title">
            Confirma tu pago
          </h1>
          <p className="text-sm text-[var(--ns-text-muted)] mt-1.5">
            Provider <code className="px-1.5 py-0.5 rounded bg-[var(--ns-bg-2)] text-[var(--ns-text)]">{session?.provider || 'mock_test'}</code>. Redsys activo cuando se selecciona en el checkout.
          </p>
        </header>

        {loading ? (
          <Skeleton className="h-72 rounded-3xl" />
        ) : !session ? (
          <Card className="p-8 rounded-3xl text-center" data-testid="checkout-not-found">
            <p className="text-[var(--ns-text-muted)]">Sesión no encontrada o ya procesada.</p>
          </Card>
        ) : session.provider === 'redsys' && session.redsys_form ? (
          <Card
            className="p-8 rounded-3xl border border-[var(--ns-border)] bg-white text-center"
            data-testid="checkout-redsys-redirect"
          >
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold mb-2">
              Redirigiendo a la pasarela segura de Redsys
            </p>
            <p className="text-lg font-semibold text-[var(--ns-text)] mb-4">
              {formatMoney(session.amount_cents, session.currency)}
            </p>
            <p className="text-sm text-[var(--ns-text-muted)] mb-6">
              Te estamos redirigiendo al TPV oficial de Redsys para completar el pago.
              No cierres esta ventana.
            </p>
            {/* Aviso genérico de apoyo al club · se reutiliza el mismo bloque
                que la rama de prueba. Visible junto al resumen y antes del
                estado/redirección cuando la sesión tiene Revenue Share. */}
            {session.has_club_revenue_share ? (
              <div
                className="flex items-start gap-2 rounded-lg bg-[var(--ns-gold)]/10 border border-[var(--ns-gold)]/25 px-3 py-2.5 mb-6 text-left"
                data-testid="checkout-club-support-notice"
              >
                <Handshake
                  className="h-4 w-4 text-[var(--ns-gold-700)] shrink-0 mt-0.5"
                  aria-hidden
                />
                <p className="text-[12px] leading-snug text-[var(--ns-text-muted)]">
                  Con tu compra también apoyas a tu club de referencia. Parte
                  del importe se comparte con tu club.
                </p>
              </div>
            ) : null}
            <Skeleton className="h-3 w-40 mx-auto rounded-full" />
            <p className="text-[11px] text-[var(--ns-text-subtle)] mt-6">
              Si no se te redirige en 5 segundos, comprueba tu conexión y vuelve a iniciar el pago.
            </p>
          </Card>
        ) : (
          <Card className="p-6 sm:p-8 rounded-3xl border border-[var(--ns-border)] bg-white" data-testid="checkout-summary">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold mb-1">
                  {SOURCE_LABEL[session.source_type] || session.source_type}
                </p>
                <p className="text-lg font-semibold text-[var(--ns-text)] break-all">
                  Ref: {session.id.slice(0, 8)}…
                </p>
              </div>
              <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text)] uppercase tracking-wider" data-testid="checkout-status">
                {session.status}
              </Badge>
            </div>
            <div className="flex items-baseline gap-2 mb-8">
              <span className="text-4xl font-bold text-[var(--ns-text)]" data-testid="checkout-amount">
                {formatMoney(session.amount_cents, session.currency)}
              </span>
              <span className="text-sm text-[var(--ns-text-muted)]">total a pagar</span>
            </div>
            {/* Aviso genérico de apoyo al club · se muestra sólo cuando el
                backend confirma que la sesión está vinculada a un club de
                referencia con Revenue Share activo (`has_club_revenue_share`).
                Estilo coherente con el aviso de las tarjetas de contenido. */}
            {session.has_club_revenue_share ? (
              <div
                className="flex items-start gap-2 rounded-lg bg-[var(--ns-gold)]/10 border border-[var(--ns-gold)]/25 px-3 py-2.5 mb-6"
                data-testid="checkout-club-support-notice"
              >
                <Handshake
                  className="h-4 w-4 text-[var(--ns-gold-700)] shrink-0 mt-0.5"
                  aria-hidden
                />
                <p className="text-[12px] leading-snug text-[var(--ns-text-muted)]">
                  Con tu compra también apoyas a tu club de referencia. Parte
                  del importe se comparte con tu club.
                </p>
              </div>
            ) : null}
            <div className="grid sm:grid-cols-2 gap-3">
              {OUTCOMES.map(({ value, label, variant, icon: Icon }) => {
                const isPrimary = variant === 'primary';
                const isDestructive = variant === 'destructive';
                const isOutline = variant === 'outline';
                const cls = isPrimary
                  ? 'bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white'
                  : isDestructive
                  ? 'bg-[var(--ns-red)] hover:bg-[var(--ns-red)]/90 text-white'
                  : isOutline
                  ? 'border-[var(--ns-border)] text-[var(--ns-text)] bg-white hover:bg-[var(--ns-bg-2)]'
                  : 'text-[var(--ns-text-muted)] hover:bg-[var(--ns-bg-2)]';
                return (
                  <Button
                    key={value}
                    type="button"
                    onClick={() => handleOutcome(value)}
                    disabled={busyOutcome !== null}
                    variant={isOutline ? 'outline' : isDestructive ? 'destructive' : 'default'}
                    className={`h-12 rounded-xl font-semibold flex items-center justify-center gap-2 ${cls}`}
                    data-testid={`checkout-outcome-${value}`}
                  >
                    <Icon className="h-4 w-4" />
                    {busyOutcome === value ? 'Procesando…' : label}
                  </Button>
                );
              })}
            </div>
            <p className="text-[11px] text-[var(--ns-text-subtle)] mt-5">
              En producción, el comportamiento real dependerá del proveedor seleccionado. Estos botones son exclusivos del entorno de pruebas.
            </p>
          </Card>
        )}
      </section>
    </MainLayout>
  );
}
